'use client';
import React, { useEffect, useRef, useState } from 'react';
import Icon from '@/components/ui/AppIcon';
import AppImage from '@/components/ui/AppImage';

const platforms = [
{
  id: 'mobile',
  icon: 'DevicePhoneMobileIcon',
  title: 'Mobile App',
  subtitle: 'Android & iOS',
  description: 'Branded customer and driver apps with real-time tracking, in-app payments, and push notifications.',
  features: ['Live GPS tracking', 'In-app booking', 'Push notifications', 'Digital receipts'],
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1b2d7861a-1772480813799.png",
  alt: 'Mobile app interface on dark smartphone showing map navigation and booking UI',
  color: '#0066FF'
},
{
  id: 'admin',
  icon: 'ComputerDesktopIcon',
  title: 'Admin Dashboard',
  subtitle: 'Web Platform',
  description: 'Comprehensive analytics, fleet oversight, driver management, and business intelligence in one place.',
  features: ['Real-time analytics', 'Driver management', 'Revenue reports', 'Trip history'],
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_138295c3b-1768067570082.png",
  alt: 'Analytics dashboard on laptop screen showing blue charts and fleet data in a bright office',
  color: '#075DDF'
},
{
  id: 'whatsapp',
  icon: 'ChatBubbleLeftRightIcon',
  title: 'WhatsApp Integration',
  subtitle: 'Booking & Support',
  description: 'Automate bookings, trip updates, and customer support via WhatsApp Business API integration.',
  features: ['Auto booking flow', 'Trip status updates', 'Support chatbot', 'Broadcast alerts'],
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_153487df8-1767720017277.png",
  alt: 'WhatsApp chat interface on phone showing automated booking conversation with green bubbles',
  color: '#075DDF'
},
{
  id: 'kiosk',
  icon: 'CreditCardIcon',
  title: 'Kiosk System',
  subtitle: 'Self-Service Terminal',
  description: 'Standalone kiosk interface for airports, malls, and transit hubs. Touch-enabled booking in seconds.',
  features: ['Touch booking', 'Cash & card pay', 'Receipt printing', 'Queue management'],
  image: "https://images.unsplash.com/photo-1734940940586-51c7d077820a",
  alt: 'Self-service kiosk terminal with bright touchscreen showing booking interface in airport terminal',
  color: '#0066FF'
},
{
  id: 'fleet',
  icon: 'TruckIcon',
  title: 'Ride Hailing',
  subtitle: 'On-Demand Ride Platform',
  description: 'Real-time ride hailing platform with intelligent driver matching, live tracking, and seamless passenger experience.',
  features: ['Instant ride booking', 'Live driver tracking', 'Driver matching', 'In-app payments'],
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1f90c4fd6-1772803707014.png",
  alt: 'Ride hailing app interface showing map with nearby drivers and booking UI',
  color: '#075DDF'
},
{
  id: 'route',
  icon: 'MapIcon',
  title: 'Route Optimizer',
  subtitle: 'Smart Dispatch Engine',
  description: 'AI-powered route optimization that reduces fuel costs, minimizes delivery time, and maximizes driver efficiency.',
  features: ['AI route planning', 'Multi-stop optimization', 'Traffic-aware routing', 'ETA predictions'],
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_19909a5f4-1767607584266.png",
  alt: 'Route optimization map showing optimized delivery paths with color-coded routes and waypoints',
  color: '#0066FF'
},
{
  id: 'corporate',
  icon: 'BuildingOffice2Icon',
  title: 'Corporate Rental',
  subtitle: 'Corporate Rental',
  description: 'Dedicated corporate account management with employee travel policies, centralized billing, and approval workflows.',
  features: ['Employee travel', 'Central billing', 'Approval flows', 'MIS reports'],
  image: "https://images.unsplash.com/photo-1724833256490-3a309b68faef",
  alt: 'Corporate office dashboard on desktop showing employee travel management and expense reports in bright workspace',
  color: '#075DDF'
},
{
  id: 'facility-pro',
  icon: 'HomeModernIcon',
  title: 'Facility Pro',
  subtitle: 'Facility Management',
  description: 'Complete facility management platform for plumbing, electrical, cleaning, HVAC, and more — with worker tracking, attendance, invoicing, and multi-branch control.',
  features: ['Work order management', 'Worker live tracking', 'Multi-branch setup', 'Invoicing & payouts'],
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1e9b025c6-1778651410372.png",
  alt: 'Home service professional with toolkit arriving at a modern home for an on-demand service booking',
  color: '#0E7490'
},
{
  id: 'food-delivery',
  icon: 'FireIcon',
  title: 'Food Delivery',
  subtitle: 'Restaurant & Delivery',
  description: 'End-to-end food delivery platform with restaurant management, real-time order tracking, and intelligent delivery dispatch.',
  features: ['Restaurant portal', 'Live order tracking', 'Smart dispatch', 'Loyalty programs'],
  image: "https://images.unsplash.com/photo-1710449827070-3ef652b7c49c",
  alt: 'Delivery rider on motorcycle carrying food bags in a busy city street during daytime',
  color: '#B45309'
},
{
  id: 'grocery',
  icon: 'ShoppingCartIcon',
  title: 'Grocery',
  subtitle: 'Grocery Delivery',
  description: 'Launch your branded grocery delivery platform with real-time inventory, slot-based delivery, and seamless store-to-door fulfillment.',
  features: ['Inventory management', 'Slot scheduling', 'Smart search', 'Demand forecasting'],
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_190549593-1768212803668.png",
  alt: 'Fresh vegetables and grocery items neatly arranged in a modern grocery store with bright lighting',
  color: '#15803D'
},
{
  id: 'single-store',
  icon: 'BuildingStorefrontIcon',
  title: 'Single Store',
  subtitle: 'Individual Store Solution',
  description: 'A powerful digital commerce solution for individual store owners to go online instantly with their own branded app and delivery operations.',
  features: ['Branded store app', 'Order management', 'Integrated payments', 'WhatsApp notifications'],
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_131575c99-1766481986821.png",
  alt: 'Small business owner using tablet to manage online orders at a modern retail store counter',
  color: '#7C3AED'
},
{
  id: 'multi-vendor',
  icon: 'Squares2X2Icon',
  title: 'Multi-Vendor',
  subtitle: 'Marketplace Platform',
  description: 'Build a full-scale multi-vendor marketplace where multiple sellers operate under your brand with centralized control and revenue management.',
  features: ['Vendor onboarding', 'Commission engine', 'Vendor dashboards', 'Automated payouts'],
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_18bd9dee4-1765625318853.png",
  alt: 'Multi-vendor marketplace interface on laptop showing multiple seller storefronts and product listings',
  color: '#BE185D'
},
{
  id: 'delivery-integrations',
  icon: 'ArrowsRightLeftIcon',
  title: 'Delivery Integrations',
  subtitle: 'Third-party Connectivity',
  description: 'Seamlessly connect with third-party delivery networks, logistics APIs, and fulfilment partners to extend your delivery reach instantly.',
  features: ['API integrations', 'Multi-partner tracking', 'Auto-routing', 'SLA monitoring'],
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_10f885e96-1778651414121.png",
  alt: 'Logistics hub with delivery vehicles and packages showing integrated delivery network operations',
  color: '#0F766E'
}];


function use3DTilt(strength = 8) {
  const ref = useRef<HTMLDivElement>(null);

  const onMove = (e: React.MouseEvent<HTMLDivElement>) => {
    const el = ref.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    const cx = rect.width / 2;
    const cy = rect.height / 2;
    const rotX = ((y - cy) / cy) * -strength;
    const rotY = ((x - cx) / cx) * strength;
    el.style.transform = `perspective(1000px) rotateX(${rotX}deg) rotateY(${rotY}deg) scale(1.02)`;
  };

  const onLeave = () => {
    const el = ref.current;
    if (!el) return;
    el.style.transition = 'transform 0.6s cubic-bezier(0.16,1,0.3,1)';
    el.style.transform = 'perspective(1000px) rotateX(0deg) rotateY(0deg) scale(1)';
    setTimeout(() => { if (el) el.style.transition = 'transform 0.15s ease, box-shadow 0.3s ease'; }, 620);
  };

  const onEnter = () => {
    const el = ref.current;
    if (!el) return;
    el.style.transition = 'transform 0.15s ease, box-shadow 0.3s ease';
  };

  return { ref, onMove, onLeave, onEnter };
}

export default function PlatformsSection() {
  const ref = useRef<HTMLElement>(null);
  const [active, setActive] = useState(0);
  const tilt = use3DTilt(7);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.querySelectorAll('.animate-on-scroll').forEach((el) => {
              el.classList.add('is-visible');
            });
          }
        });
      },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  const platform = platforms[active];

  return (
    <section ref={ref} className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="animate-on-scroll stagger-1 mb-16 flex flex-col md:flex-row md:items-end md:justify-between gap-6">
          <div>
            <span className="section-label block mb-3">Multi-Platform</span>
            <h2 className="font-display text-4xl md:text-5xl font-semibold text-[#0F172A] tracking-tight leading-tight">
              One platform,{' '}
              <span className="italic font-light text-[#0066FF]">every channel.</span>
            </h2>
          </div>
          <p className="text-[#475569] max-w-sm leading-relaxed">
            Deploy your mobility brand across mobile, web, WhatsApp, kiosk, and corporate portals — all from a single backend.
          </p>
        </div>

        {/* Platform tabs */}
        <div className="animate-on-scroll stagger-2 flex gap-2 overflow-x-auto pb-2 mb-8 scrollbar-hide">
          {platforms.map((p, i) =>
          <button
            key={p.id}
            onClick={() => setActive(i)}
            className={`flex-shrink-0 flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium transition-all duration-200 ${
            active === i ?
            'text-white shadow-blue-sm' :
            'text-[#475569] bg-[#FFFFFF] hover:bg-[#FFFFFF]'}`
            }
            style={active === i ? { background: 'linear-gradient(135deg, #0066FF, #075DDF)' } : {}}>
            
              <Icon name={p.icon as Parameters<typeof Icon>[0]['name']} size={16} />
              {p.title}
            </button>
          )}
        </div>

        {/* Platform detail */}
        <div className="animate-on-scroll stagger-3 grid lg:grid-cols-2 gap-8 items-center">
          {/* Info */}
          <div className="flex flex-col gap-6">
            <div>
              <span className="tag-badge mb-4 inline-flex">{platform.subtitle}</span>
              <h3 className="font-display text-3xl md:text-4xl font-semibold text-[#0F172A] mb-4 leading-tight">
                {platform.title}
              </h3>
              <p className="text-[#475569] text-lg leading-relaxed">{platform.description}</p>
            </div>

            <div className="grid grid-cols-2 gap-3">
              {platform.features.map((f) =>
              <div key={f} className="flex items-center gap-2.5 p-3 bg-[#FFFFFF] rounded-xl border border-[rgba(15,23,42,0.06)]">
                  <div className="w-6 h-6 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: 'linear-gradient(135deg, #0066FF20, #075DDF10)' }}>
                    <Icon name="CheckIcon" size={14} className="text-[#0066FF]" />
                  </div>
                  <span className="text-sm font-medium text-[#0F172A]">{f}</span>
                </div>
              )}
            </div>
          </div>

          {/* Image mockup */}
          <div
            ref={tilt.ref}
            onMouseMove={tilt.onMove}
            onMouseLeave={tilt.onLeave}
            onMouseEnter={tilt.onEnter}
            className="tilt-3d relative rounded-2xl overflow-hidden shadow-blue aspect-[4/3]"
            style={{ transformStyle: 'preserve-3d' }}
          >
            <AppImage
              src={platform.image}
              alt={platform.alt}
              fill
              className="object-cover"
              sizes="(max-width: 768px) 100vw, 50vw" />
            
            <div className="absolute inset-0" style={{ background: 'linear-gradient(to top right, rgba(10,22,40,0.5), transparent)' }} />
            <div className="badge-3d absolute bottom-4 left-4 right-4">
              <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-xl p-3 flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #0066FF, #075DDF)' }}>
                  <Icon name={platform.icon as Parameters<typeof Icon>[0]['name']} size={16} className="text-white" />
                </div>
                <div>
                  <div className="text-white text-xs font-semibold">{platform.title}</div>
                  <div className="text-white/60 text-[10px]">{platform.subtitle}</div>
                </div>
                <div className="ml-auto flex items-center gap-1.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
                  <span className="text-emerald-300 text-[10px] font-medium">Live</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>);

}